import { CheckIcon, PencilIcon, PlusIcon, TrashIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useId, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { signAsset } from '@/api/assets';
import { deleteProjectTemplateTdm, patchProjectTemplateBulkTDMs, patchProjectTemplateTdm } from '@/api/Sherpa/ProjectTemplate';
import { LayoutCard, LayoutCardContent, LayoutCardTitle } from '@/components/LayoutCard';
import Button from '@/components/UI/Button';
import Input from '@/components/UI/Form/Input';
import ReactSelect from '@/components/UI/Form/ReactSelect';
import Confirm from '@/components/UI/Modal/Confirm';
import { useProjectTemplateTdm } from '@/hooks/ReactQueryHooks/useProjectTemplateQuery';
import { useOrgTitles } from '@/hooks/ReactQueryHooks/useTitlesQuery';
import { useFileUpload } from '@/hooks/useFileUpload';
import { notifyUser } from '@/store/Toast';
import type { TitleInterface } from '@/types/Account';
import { getAcceptedFileTypes, isObjectEmpty, sanitizeResponseError } from '@/utils';
import EditableCell from '../../EditableCell';
import Table from '../../Table';
import UpdateTemplateForm from '../UpdateTemplateForm';
import NewMilestoneForm from './NewMilestoneForm';

interface ProjectTemplateMilestonesTableProps {
  activeNav: { name: string; slug: string };
}

const ProjectTemplateMilestonesTable = ({ activeNav }: ProjectTemplateMilestonesTableProps) => {
  const { templateId } = useParams();
  const dispatch = useDispatch();

  const templateNoteRef = useRef<HTMLInputElement | null>(null);
  const leadTitleSelectId = useId();
  const supportTitleSelectId = useId();
  const [isDragged, setIsDragged] = useState<boolean>(false);

  const queryClient = useQueryClient();
  const [_skipPageReset, setSkipPageReset] = useState(false);
  const [data, setData] = useState<any>([]);
  const [params, setParams] = useState<any>({
    perPage: 10,
    page: 1,
    orderBy: 'sortIndex',
    order: 'asc',
  });
  const [showModal, setShowModal] = useState({
    newMilestoneForm: false,
    updateTemplateForm: false,
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [selectedTemplateMilestone, setSelectedTemplateMilestone] = useState<any>();
  const [touchedInput, setTouchedInput] = useState<{
    [key: string]: boolean;
  }>();
  const [selectedRowId, setSelectedRowId] = useState<number>();
  const [selectedTemplateFile, setSelectedTemplateFiles] = useState<{ name: string; key: string; contentType: string; size: string }[]>([]);

  const fetchOrgTitles = useOrgTitles({});
  const titles = fetchOrgTitles?.data?.data;

  const fetchProjectTemplateMilestones = useProjectTemplateTdm({
    templateId: Number(templateId),
    params: { ...params, phase: activeNav.slug, resourceType: 'MILESTONE' },
  });

  const projectTemplateMilestones = fetchProjectTemplateMilestones?.data?.data;

  const projectTemplateMilestonesCount = fetchProjectTemplateMilestones?.data?.meta?.total ?? 0;

  const pageCount = Math.ceil(projectTemplateMilestonesCount / params.perPage);

  useEffect(() => {
    setData(projectTemplateMilestones);
  }, [projectTemplateMilestones]);

  const cancelUpdate = () => {
    setData(projectTemplateMilestones);
  };
  const { upload } = useFileUpload({ signAsset: signAsset });

  const uploadFile = useMutation(
    (payload: {
      signAssetPayload: {
        key: string;
        type: string;
        resource: string;
      }[];
      files: File[];
    }) => upload(payload),
    {
      onSuccess: (response) => {
        const assetsFiles = response?.files.map((file: any, index: number) => {
          return {
            name: file.name,
            key: response?.sign.fKey ?? response.sign[index].fKey,
            contentType: file.type,
            size: file.size,
          };
        });

        updateProjectTemplateMilestone.mutate({
          data: {
            templateFile: assetsFiles
              ? selectedTemplateFile.length
                ? assetsFiles.concat(selectedTemplateFile)
                : assetsFiles
              : selectedTemplateFile,
          },
          tdmId: selectedRowId!,
          templateId: Number(templateId),
        });
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const handleFileUpload = (selectedFiles: File[]) => {
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    selectedFiles.forEach((file: File) => {
      const acceptedFileTypes = getAcceptedFileTypes().split(',');
      if (acceptedFileTypes.includes(file.type)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `${invalidFiles.join(', ')} file types are not accepted to upload.`,
          toastType: 'failed',
        })
      );
    }

    if (validFiles.length > 0) {
      const signPayload = validFiles.map<{
        key: string;
        type: string;
        resource: string;
      }>((file) => ({
        key: file.name,
        type: file.type,
        resource: 'templates',
      }));

      uploadFile.mutate({
        signAssetPayload: signPayload,
        files: validFiles,
      });
    }
  };

  const handleSelectedTemplateFile = (data: any) => {
    setSelectedRowId(data?.id);
    if (data?.templateFile) {
      setSelectedTemplateFiles(data?.templateFile);
    }
    handleUpdateFormModal();
  };

  const handleDeleteFile = (key: string) => {
    if (!key) return;

    const newTemplateFile = selectedTemplateFile.filter((file) => file.key !== key);
    setSelectedTemplateFiles(newTemplateFile);

    updateProjectTemplateMilestone.mutate({
      data: { templateFile: newTemplateFile },
      tdmId: selectedRowId!,
      templateId: Number(templateId),
    });
  };

  const handleModal = () => {
    setShowModal((prevState) => ({
      ...prevState,
      newMilestoneForm: !prevState.newMilestoneForm,
    }));
  };

  const handleUpdateFormModal = () => {
    setShowModal((prevState) => ({
      ...prevState,
      updateTemplateForm: !prevState.updateTemplateForm,
    }));
  };

  const confirmDeleteProjectTemplateMilestone = async () => {
    if (!selectedTemplateMilestone) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Invalid project template chosen.',
          toastType: 'failed',
        })
      );
    } else {
      removeProjectTemplateMilestone.mutate({
        templateId: Number(templateId),
        data: { resourceType: 'MILESTONE' },
        tdmId: selectedTemplateMilestone?.id,
      });
    }
  };

  const cancelDeleteProjectTemplateMilestone = () => {
    setShowDeleteConfirm(false);
  };

  const removeProjectTemplateMilestone = useMutation(
    (data: { templateId: number; tdmId: number; data: { resourceType: string } }) =>
      deleteProjectTemplateTdm({
        templateId: data.templateId,
        tdmId: data.tdmId,
        data: data.data,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['get-project-template-tdm']);
        setShowDeleteConfirm(false);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Project Template milestone deleted successfully',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const updateProjectTemplateMilestone = useMutation(
    (payload: { data: any; templateId: number; tdmId: number }) =>
      patchProjectTemplateTdm({
        data: { resourceType: 'MILESTONE', templateTdm: payload.data },
        templateId: payload.templateId,
        tdmId: payload.tdmId,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['get-project-template-tdm']);
        setTouchedInput({});
        setShowDeleteConfirm(false);
        if (showModal.updateTemplateForm) handleUpdateFormModal();
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Project Template milestone saved successfully',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        cancelUpdate();
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const handleSave = (data: any) => {
    const { id, milestone, leadTitle, supportTitle, note } = data;
    const updatedData = {
      ...(touchedInput?.milestone && { milestone }),
      ...(touchedInput?.leadTitle && leadTitle && { leadTitleId: parseInt(leadTitle.id, 10) }),
      ...(touchedInput?.supportTitle && supportTitle && { supportTitleId: parseInt(supportTitle.id, 10) }),
      ...(touchedInput?.note && { note }),
    };
    if (!isObjectEmpty(updatedData)) {
      updateProjectTemplateMilestone.mutate({
        data: updatedData,
        tdmId: id,
        templateId: Number(templateId),
      });
    } else {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'No changes were detected to save the milestone.',
          toastType: 'info',
        })
      );
    }
  };

  const updateTableData = (rowIndex: number, columnId: string, value: TitleInterface | string | { name: string; id: string }) => {
    // We also turn on the flag to not reset the page
    setTouchedInput((prevState) => ({ ...prevState, [columnId]: true }));
    setSkipPageReset(true);
    setData((old: any) =>
      old.map((row: any, index: number) => {
        if (index === rowIndex) {
          return {
            ...old[rowIndex],
            [columnId]: value,
          };
        }
        return row;
      })
    );
  };

  const defaultColumn = {
    cell: EditableCell,
  };

  const nextPage = () => {
    const nextPage = params.page + 1;
    if (nextPage <= pageCount) {
      setParams((prev: any) => ({ ...prev, page: nextPage }));
    }
  };
  const previousPage = () => {
    const previousPage = params.page - 1;
    if (previousPage > 0) {
      setParams((prev: any) => ({ ...prev, page: previousPage }));
    }
  };

  const tablePagination = {
    pageCount,
    pageSize: params.perPage,
    currentPage: params.page,
    page: params.page,
    setPageSize: (pageSize: number) =>
      setParams((params: any) => ({
        ...params,
        page: 1,
        perPage: pageSize,
      })),
    nextPage,
    previousPage,
  };

  const tableColumns: ColumnDef<any>[] = [
    {
      id: 'milestone',
      header: 'Milestone Name',
      accessorKey: 'milestone',
    },
    {
      id: 'leadTitle',
      header: 'Lead Organizational Title',
      accessorKey: 'leadTitle',
      cell: (props) => {
        const { row, getValue, table, column } = props;
        const leadTitleValue = getValue() as TitleInterface | undefined;
        const meta = table.options.meta as any;
        const editableRowIndex = meta?.editableRowIndex;

        if (editableRowIndex !== row.index) {
          return <div>{leadTitleValue?.name}</div>;
        } else {
          return (
            <ReactSelect<{ name: string; id: string }>
              id={`${leadTitleSelectId}-${row.index}`}
              name='leadTitle'
              options={
                titles
                  ?.filter((t) => t.id !== row.original?.supportTitle?.id)
                  .map((t) => ({
                    name: t.name,
                    id: t.id.toString(),
                  })) || []
              }
              defaultValue={
                leadTitleValue
                  ? {
                      name: leadTitleValue.name,
                      id: leadTitleValue.id?.toString() || '',
                    }
                  : undefined
              }
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              onChange={(option) => {
                if (option?.name && option?.id) {
                  updateTableData(row.index, column.id, option);
                }
              }}
            />
          );
        }
      },
    },
    {
      id: 'supportTitle',
      header: 'Support Organizational Title',
      accessorKey: 'supportTitle',
      cell: (props) => {
        const { row, getValue, table, column } = props;
        const supportTitleValue = getValue() as TitleInterface | undefined;
        const meta = table.options.meta as any;
        const editableRowIndex = meta?.editableRowIndex;

        if (editableRowIndex !== row.index) {
          return <div>{supportTitleValue?.name}</div>;
        } else {
          return (
            <ReactSelect<{ name: string; id: string }>
              id={`${supportTitleSelectId}-${row.index}`}
              name='supportTitle'
              options={
                titles
                  ?.filter((t) => t.id !== row.original?.leadTitle?.id)
                  .map((t) => ({
                    name: t.name,
                    id: t.id.toString(),
                  })) || []
              }
              defaultValue={
                supportTitleValue
                  ? {
                      name: supportTitleValue.name,
                      id: supportTitleValue.id?.toString() || '',
                    }
                  : undefined
              }
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              onChange={(option) => {
                if (option?.name && option?.id) {
                  updateTableData(row.index, column.id, option);
                }
              }}
            />
          );
        }
      },
    },
    {
      id: 'note',
      header: 'Notes',
      accessorKey: 'note',
      cell: (props) => {
        const { row, getValue, table, column } = props;
        const template = row.original;
        const noteValue = getValue() as string | undefined;
        const meta = table.options.meta as any;
        const editableRowIndex = meta?.editableRowIndex;

        if (editableRowIndex !== row.index) {
          return <p className='max-w-xl break-words line-clamp-3'>{noteValue}</p>;
        } else {
          return (
            <div className='w-72 xl:w-96 relative'>
              <Input
                ref={templateNoteRef}
                type='text'
                name={template.id}
                defaultValue={template.note}
                onBlur={() => {
                  if (templateNoteRef?.current?.value) {
                    updateTableData(row.index, column.id, templateNoteRef.current.value);
                  } else {
                    updateTableData(row.index, column.id, '');
                  }
                }}
              />
            </div>
          );
        }
      },
    },
    {
      id: 'template',
      header: 'Template',
      accessorKey: 'templateFile',
      cell: (props) => {
        const { row, getValue } = props;
        const templateFileValue = getValue() as { name: string; key: string; contentType: string; size: string }[] | undefined;
        return templateFileValue && templateFileValue.length > 0 ? (
          <div className='underline' onClick={() => handleSelectedTemplateFile(row.original)}>
            {templateFileValue.length > 1 ? `${templateFileValue.length} Files` : `${templateFileValue.length} File`}{' '}
          </div>
        ) : (
          <div className='underline' onClick={() => handleSelectedTemplateFile(row.original)}>
            No File
          </div>
        );
      },
    },
    {
      id: 'edit',
      header: '',
      accessorKey: 'edit',
      size: 20,
      cell: (props) => {
        const { row, table } = props;
        const templateMilestone = row.original;
        const meta = table.options.meta as any;
        const editableRowIndex = meta?.editableRowIndex;
        const setEditableRowIndex = meta?.setEditableRowIndex;

        if (editableRowIndex !== row.index) {
          return (
            <div className='text-neutralColor-normal flex justify-center gap-4'>
              <span
                onClick={() => {
                  setEditableRowIndex?.(row.index);
                }}
              >
                <PencilIcon className='h-5 w-5 hover:text-primaryColor-normal transition duration-300 ease-in-out' />
              </span>
              <span
                onClick={() => {
                  setSelectedTemplateMilestone(templateMilestone);
                  setShowDeleteConfirm(true);
                }}
              >
                <TrashIcon className='h-5 w-5 hover:text-tertiaryColor-normal transition duration-300 ease-in-out' />
              </span>
            </div>
          );
        } else {
          return (
            <div className='flex justify-center gap-4'>
              <span
                className='text-primaryColor-normal bg-neutralColor-lightest hover:bg-neutralColor-lighter rounded-full p-1 cursor-pointer'
                onClick={() => {
                  setEditableRowIndex?.(null);
                  handleSave(templateMilestone);
                }}
              >
                <CheckIcon className='h-5 w-5' />
              </span>
              <span
                className='text-tertiaryColor-normal bg-neutralColor-lightest hover:bg-neutralColor-lighter rounded-full p-1 cursor-pointer'
                onClick={() => {
                  setEditableRowIndex?.(null);
                }}
              >
                <XCircleIcon className='h-5 w-5' />
              </span>
            </div>
          );
        }
      },
    },
  ];

  const updateProjectTemplateBulkMilestones = useMutation(
    (payload: { data: any; templateId: number }) =>
      patchProjectTemplateBulkTDMs({
        data: { resourceType: 'MILESTONE', templateTdms: payload.data },
        templateId: payload.templateId,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['get-project-template-tdm']);
        setTouchedInput({});
        setShowDeleteConfirm(false);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Milestone table updated successfully',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        cancelUpdate();
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const handleChanges = (btnType: string) => {
    setIsDragged(false);

    const tasksWithIdAndIndex = data.map((d: any) => {
      return { id: d.id, sortIndex: d.sortIndex };
    });
    if (btnType === 'save') {
      updateProjectTemplateBulkMilestones.mutate({
        data: tasksWithIdAndIndex,
        templateId: Number(templateId),
      });
    } else {
      setData(projectTemplateMilestones);
    }
  };

  return (
    <>
      <LayoutCard>
        <LayoutCardTitle
          title={`${activeNav.name} Milestones`}
          actions={
            isDragged ? (
              <div className='flex gap-2'>
                <Button label='Save' btnStyle='btn-primary' type='button' onClick={() => handleChanges('save')} />
                <Button label='Cancel' btnStyle='btn-tertiary' type='button' onClick={() => handleChanges('cancel')} />
              </div>
            ) : (
              <Button
                label='New Milestone'
                icon={<PlusIcon className='h-5 w-5' />}
                btnStyle='btn-primary'
                type='button'
                onClick={handleModal}
              />
            )
          }
        />
        <LayoutCardContent>
          {data && (
            <Table
              cols={tableColumns}
              defaultColumn={defaultColumn}
              tableData={data}
              updateTableData={updateTableData}
              pagination={tablePagination}
              tdmType='deliverable'
              setIsDragged={setIsDragged}
              setData={setData}
            />
          )}
        </LayoutCardContent>
      </LayoutCard>
      {showModal.newMilestoneForm && <NewMilestoneForm phase={activeNav.slug} handleModal={handleModal} />}
      {showModal.updateTemplateForm && (
        <UpdateTemplateForm
          handleTemplateModal={handleUpdateFormModal}
          data={selectedTemplateFile}
          handleDeleteFile={handleDeleteFile}
          handleFileUpload={handleFileUpload}
          isLoading={uploadFile.isLoading}
        />
      )}
      {showDeleteConfirm && (
        <Confirm
          title='Delete Template'
          message={'You are about to delete project template milestone. Are you sure you want to delete ?'}
          acceptButtonLabel={`${removeProjectTemplateMilestone.isLoading ? 'Deleting...' : 'Delete'}`}
          declineButtonLabel='Cancel'
          accept={confirmDeleteProjectTemplateMilestone}
          decline={cancelDeleteProjectTemplateMilestone}
        />
      )}
    </>
  );
};

export default ProjectTemplateMilestonesTable;
